import { api } from '@packages/backend/convex/_generated/api';
import { useConvex } from 'convex/react';
import { useCallback } from 'react';
import {
  type WorldlineCreatePaymentRequest,
  type WorldlineCreatePaymentResult,
  WorldlinePaymentForm,
} from './WorldlinePaymentForm';
import { setWorldlineRedirectState } from './worldlineRedirectState';

type SessionWorldlinePaymentFormProps = {
  sessionUuid: string;
  amountCents: number;
  worldlineHost: string;
  submitLabel: string;
  onSuccess: () => void;
  onError: (message: string) => void;
  onSubmitting?: () => void;
};

export const SessionWorldlinePaymentForm = ({
  sessionUuid,
  amountCents,
  worldlineHost,
  submitLabel,
  onSuccess,
  onError,
  onSubmitting,
}: SessionWorldlinePaymentFormProps) => {
  const convex = useConvex();

  const createTokenizationSession = useCallback(async () => {
    return await convex.action(api.payment.payments.createWorldlinePaymentTokenizationSession, {
      sessionUuid,
    });
  }, [convex, sessionUuid]);

  const createPayment = useCallback(
    async (args: WorldlineCreatePaymentRequest): Promise<WorldlineCreatePaymentResult> => {
      const result = await convex.action(api.payment.payments.createWorldlinePayment, {
        sessionUuid,
        amountCents,
        ...args,
      });

      if (result.status === 'SUCCESS') {
        return { status: 'SUCCESS' };
      }

      if (result.status === 'REDIRECT') {
        return {
          status: 'REDIRECT',
          paymentGatewayPaymentId: result.paymentGatewayPaymentId,
          redirectUrl: result.redirectUrl,
        };
      }

      return {
        status: 'FAILED',
        errorMessage: result.errorMessage,
      };
    },
    [amountCents, convex, sessionUuid],
  );

  const handleRedirect = useCallback(
    (result: Extract<WorldlineCreatePaymentResult, { status: 'REDIRECT' }>) => {
      setWorldlineRedirectState(sessionUuid, {
        paymentGatewayPaymentId: result.paymentGatewayPaymentId,
        amountCents,
      });
      window.location.assign(result.redirectUrl);
    },
    [amountCents, sessionUuid],
  );

  return (
    <WorldlinePaymentForm
      tokenizerUrl={`https://${worldlineHost}/hostedtokenization/js/client/tokenizer.min.js`}
      submitLabel={submitLabel}
      createTokenizationSession={createTokenizationSession}
      createPayment={createPayment}
      onSuccess={onSuccess}
      onRedirect={handleRedirect}
      onError={onError}
      onSubmitting={onSubmitting}
    />
  );
};
